import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import AppShell from './components/layout/AppShell';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Contacts from './pages/Contacts';
import ContactDetail from './pages/ContactDetail';
import Analyses from './pages/Analyses';
import AnalysisReport from './pages/AnalysisReport';
import AnalysisClusters from './pages/AnalysisClusters';
import AnalysisCluster from './pages/AnalysisCluster';
import CroMeasures from './pages/CroMeasures';
import Reports from './pages/Reports';
import ShareLinks from './pages/ShareLinks';
import Analytics from './pages/Analytics';
import KnowledgeBase from './pages/KnowledgeBase';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Factors from './pages/Factors';
import Bookmarklet from './pages/Bookmarklet';
import SeoRankingChecker from './pages/SeoRankingChecker';
import SeoMeasures from './pages/SeoMeasures';
import SeoWeekly from './pages/SeoWeekly';
import Projects from './pages/Projects';
import ProjectReports from './pages/ProjectReports';
import ShareProjectReport from './pages/ShareProjectReport';
import ShareAnalysisCluster from './pages/ShareAnalysisCluster';
import GlobalOverview from './pages/GlobalOverview';
import ComingSoon from './pages/ComingSoon';
import PlatformHome from './pages/PlatformHome';
import CockpitDashboard from './pages/CockpitDashboard';
import ShareCockpit from './pages/ShareCockpit';
import AnalyticsGa4 from './pages/AnalyticsGa4';
import AnalyticsTracking from './pages/AnalyticsTracking';
import LiteTracking from './pages/LiteTracking';
import Integrations from './pages/Integrations';
import AgentLibrary from './pages/AgentLibrary';
import AgentOverview from './pages/AgentOverview';
import AgentPipelines from './pages/AgentPipelines';
import CustomerAgents from './pages/CustomerAgents';
import SeoCheck from './pages/SeoCheck';
import SeoChecklist from './pages/SeoChecklist';
import Massnahmen from './pages/Massnahmen';
import Report from './pages/Report';
import ShareReport from './pages/ShareReport';
import Systemstatus from './pages/Systemstatus';
import Onboarding from './pages/Onboarding';

function LoadingScreen() {
  return (
    <div className="flex items-center justify-center h-screen bg-bg">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-teal-light text-teal flex items-center justify-center text-xl mx-auto mb-4 animate-pulse">⟳</div>
        <div className="text-[14px] text-gray-500">Wird geladen…</div>
      </div>
    </div>
  );
}

export default function App() {
  const { user, loading } = useAuth();
  const isPublicShare = window.location.pathname.startsWith('/share/');

  if (loading && !isPublicShare) return <LoadingScreen />;

  if (isPublicShare) {
    return (
      <Routes>
        <Route path="/share/project-report/:token" element={<ShareProjectReport />} />
        <Route path="/share/cluster/:token" element={<ShareAnalysisCluster />} />
        <Route path="/share/cockpit/:token" element={<ShareCockpit />} />
        <Route path="/share/report/:token" element={<ShareReport />} />
      </Routes>
    );
  }

  if (!user) return <Login />;

  // Lite-Kunden (cro_role='lite') bekommen ein stark reduziertes Routen-Set:
  // nur den eigenen Kontakt + Tracking-Tab, keine Analyses/Cluster/Measures/Settings.
  if (user.cro_role === 'lite') {
    return (
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<LiteTracking />} />
          <Route path="contacts/:id" element={<ContactDetail />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        {/* Projekt-Übersicht (Dashboard im Projektkontext) */}
        <Route index element={<PlatformHome />} />
        <Route path="projekt-uebersicht" element={<Dashboard />} />
        <Route path="agenten" element={<CustomerAgents />} />
        <Route path="project-reports" element={<ProjectReports />} />

        {/* CRO (projektspezifisch) */}
        <Route path="cro/analyses" element={<Analyses />} />
        <Route path="cro/analyses/:id" element={<AnalysisReport />} />
        <Route path="cro/clusters" element={<AnalysisClusters />} />
        <Route path="cro/clusters/:id" element={<AnalysisCluster />} />
        <Route path="cro/measures" element={<CroMeasures />} />
        <Route path="cro/reports" element={<Reports />} />
        <Route path="cro/share-links" element={<ShareLinks />} />

        {/* SEO (projektspezifisch) */}
        <Route path="seo/check" element={<SeoCheck />} />
        <Route path="seo/checkliste" element={<SeoChecklist />} />
        <Route path="report" element={<Report />} />
        <Route path="seo/rankings" element={<SeoRankingChecker />} />
        <Route path="seo/measures" element={<Massnahmen />} />
        <Route path="seo/weekly" element={<SeoWeekly />} />

        {/* Analytics / CRO — projektspezifische Dashboards */}
        <Route path="analytics/ga4" element={<AnalyticsGa4 />} />
        <Route path="analytics/tracking" element={<AnalyticsTracking />} />

        {/* SEA (projektspezifisch, Google Ads) */}
        <Route
          path="sea/campaigns"
          element={
            <ComingSoon
              title="SEA · Kampagnen"
              icon="⊙"
              description="Google-Ads-Kampagnen, Budgets und Performance landen hier, sobald das SEA-Modul freigeschaltet ist."
            />
          }
        />
        <Route
          path="sea/keywords"
          element={
            <ComingSoon
              title="SEA · Keywords"
              icon="◇"
              description="Keyword-Performance, Quality Score und Suchbegriffs-Report aus Google Ads."
            />
          }
        />

        {/* Agent Center — Library (Templates+Skills) + Agent Library (optische Übersicht) + Pipelines */}
        <Route path="agent-center/library" element={<AgentLibrary />} />
        <Route path="agent-center/agents" element={<AgentOverview />} />
        <Route path="agent-center" element={<AgentPipelines />} />
        {/* Legacy-Aliase: alte Sub-Routen leiten auf den Projekt-Agents-Screen */}
        <Route path="agent-center/seo" element={<Navigate to="/agent-center" replace />} />
        <Route path="agent-center/sea" element={<Navigate to="/agent-center" replace />} />
        <Route path="agent-center/cro" element={<Navigate to="/agent-center" replace />} />
        <Route path="agent-center/tracking" element={<Navigate to="/agent-center" replace />} />
        <Route path="agent-center/reporting" element={<Navigate to="/agent-center" replace />} />

        {/* Kunden Cockpit (projektspezifisch, natives Dashboard) */}
        <Route path="cockpit" element={<CockpitDashboard />} />

        {/* Projekt-spezifisch (Knowledge) */}
        <Route path="knowledge" element={<KnowledgeBase />} />

        {/* Global */}
        <Route path="overview" element={<GlobalOverview />} />
        <Route path="projects" element={<Projects />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="contacts/:id" element={<ContactDetail />} />
        <Route path="factors" element={<Factors />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="users" element={<Users />} />
        <Route path="systemstatus" element={<Systemstatus />} />
        <Route path="bookmarklet" element={<Bookmarklet />} />
        <Route path="settings" element={<Settings />} />
        <Route path="integrations" element={<Integrations />} />

        {/* Legacy-Aliase (alte URLs weiterhin unterstützen) */}
        <Route path="analyses" element={<Navigate to="/cro/analyses" replace />} />
        <Route path="analyses/:id" element={<Navigate to="/cro/analyses/:id" replace />} />
        <Route path="reports" element={<Navigate to="/cro/reports" replace />} />
        <Route path="share-links" element={<Navigate to="/cro/share-links" replace />} />
        <Route path="seo" element={<Navigate to="/seo/rankings" replace />} />
        <Route path="seo/massnahmen" element={<Navigate to="/seo/measures" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
